/**
 * Utility functions for the training module
 */

/**
 * Calculates the status of a module based on its attempts
 *
 * @param {object} module - The training module
 * @returns {object} Status object with passed, inProgress, notStarted, and required flags
 */
export const calculateModuleStatus = module => {
  const attempts = module.training_user_attempt_index || [];

  return {
    passed: attempts.some(attempt => attempt.pass === true),
    inProgress:
      attempts.length > 0 && !attempts.some(attempt => attempt.pass === true),
    notStarted: attempts.length === 0,
    required: module.is_compulsory,
  };
};

/**
 * Gets the best attempt score for a module
 *
 * @param {object} module - The training module
 * @returns {number} The best attempt score as a percentage
 */
export const getBestAttemptScore = module => {
  const attempts = module.training_user_attempt_index || [];

  if (attempts.length === 0) return 0;

  return Math.max(...attempts.map(attempt => attempt.percentage));
};

/**
 * Gets the average attempt score for a module
 *
 * @param {object} module - The training module
 * @returns {number} The average attempt score as a percentage
 */
export const getAverageAttemptScore = module => {
  const attempts = module.training_user_attempt_index || [];

  if (attempts.length === 0) return 0;

  const total = attempts.reduce((sum, attempt) => sum + attempt.percentage, 0);
  return total / attempts.length;
};

/**
 * Formats a list of modules for display by adding status and other properties
 *
 * @param {Array} modules - Array of training modules
 * @returns {Array} Modules with added status and other properties
 */
export const formatModulesForDisplay = modules => {
  return modules.map(module => {
    // Calculate status
    const status = calculateModuleStatus(module);

    // Extract suppliers and product types
    const modSuppliers = module.training_supplier_index
      ? module.training_supplier_index.map(sup => sup.suppliers_master_id)
      : [];

    const modProductTypes = module.training_refs_product_type_index
      ? module.training_refs_product_type_index.map(
          pt => pt.refs_product_type_id
        )
      : [];

    return {
      ...module,
      module_suppliers: modSuppliers,
      module_product_types: modProductTypes,
      status,
    };
  });
};

/**
 * Calculates overall progress statistics
 *
 * @param {Array} modules - Array of training modules
 * @returns {object} Progress statistics
 */
export const calculateOverallProgress = modules => {
  const totalModules = modules.length;

  const completedModules = modules.filter(
    module =>
      module.training_user_attempt_index &&
      module.training_user_attempt_index.some(attempt => attempt.pass)
  ).length;

  const inProgressModules = modules.filter(
    module =>
      module.training_user_attempt_index &&
      module.training_user_attempt_index.length > 0 &&
      !module.training_user_attempt_index.some(attempt => attempt.pass)
  ).length;

  const notStartedModules = totalModules - completedModules - inProgressModules;

  const progressPercentage =
    totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  return {
    total: totalModules,
    completed: completedModules,
    inProgress: inProgressModules,
    notStarted: notStartedModules,
    percentage: progressPercentage,
  };
};

/**
 * Generates unique categories from a list of modules
 *
 * @param {Array} modules - Array of training modules
 * @returns {Array} Array of unique categories as { label, value } objects
 */
export const generateCategoryOptions = modules => {
  const categories = new Set();

  modules.forEach(module => {
    if (module.module_category) {
      categories.add(module.module_category);
    }
  });

  return Array.from(categories).map(category => ({
    label: category,
    value: category,
  }));
};

/**
 * Formats supplier data as options for select fields
 *
 * @param {Array} suppliers - Array of supplier objects
 * @returns {Array} Array of suppliers as { label, value } objects
 */
export const formatSupplierOptions = suppliers => {
  return suppliers.map(supplier => ({
    label: supplier.supplier_name,
    value: supplier.id,
  }));
};
